// @ts-check

import React from 'react';

import Box from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import TriangleAlertIcon from '@liquidweb-dev/undercurrent/icons/lucide/TriangleAlert';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import FancyHighlight from 'components/FancyHighlight';
import useBillingBalance from 'modules/queries/billing/useBalance';
import { Link as RouterLink } from 'react-router-dom';
import RestrictionPaper from '../RestrictionPaper';
import RestrictionWrapper from '../RestrictionWrapper';

/** @param {import('@liquidweb-dev/undercurrent/Stack').StackProps} props */
const RestrictionSuspended = ({ sx, ...rest }) => {
	const { data: billingBalanceData, isLoading } = useBillingBalance();

	const pastDue = billingBalanceData?.pastDue;

	return (
		<RestrictionWrapper
			sx={combineSx(
				({ vars }) => ({
					background: `linear-gradient(165deg, ${vars.palette.uc.bg.main} 10.48%, ${vars.palette.uc.danger.bg.main} 55.27%, ${vars.palette.uc.bg.main} 115.69%)`,
				}),
				sx,
			)}
			{...rest}
		>
			<RestrictionPaper>
				{isLoading ? (
					<Skeleton variant="rounded" sx={{ width: '220px', height: '34px' }} />
				) : (
					<FancyHighlight
						sx={({ vars }) => ({
							display: 'flex',
							gap: 100,
							alignItems: 'center',
							width: 'fit-content',
							color: vars.palette.uc.danger.text.main,
							borderColor: vars.palette.uc.danger.border.main,
							backgroundColor: vars.palette.uc.danger.bg.main,
						})}
					>
						<TriangleAlertIcon sx={{ width: '20px', height: '20px' }} />
						<Typography variant="title-sm">
							{formatCurrency(pastDue)} past due
						</Typography>
					</FancyHighlight>
				)}

				<Stack sx={{ gap: 200 }}>
					<Typography variant="body-md" sx={{ fontWeight: 500 }}>
						We&apos;ve temporarily restricted access to your account.
					</Typography>

					<Typography
						variant="body-sm"
						sx={{ color: ({ vars }) => vars.palette.uc.text.medium }}
					>
						This can happen for a few reasons,{' '}
						<Typography component="span" sx={{ fontWeight: 700 }}>
							such as billing issues
						</Typography>
						. Let&apos;s sort it out together. Start by checking your Billing
						Center for more info. If you still need help, feel free to reach out
						to support.
					</Typography>
				</Stack>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: 100 }}>
					<Button component={RouterLink} to="/account/billing" size="large">
						Billing center
					</Button>
					<Button
						variant="secondary"
						component={RouterLink}
						to="/support/mycases"
						size="large"
					>
						Support center
					</Button>
				</Box>
			</RestrictionPaper>
		</RestrictionWrapper>
	);
};

export default RestrictionSuspended;
